(function () {
  function log() {}

  function safeSubscribe(eventName, callback) {
    if (typeof subscribe === 'function') {
      return subscribe(eventName, callback);
    }
    console.warn('subscribe not ready yet, retrying…', eventName);
    let attempts = 0;
    let unsubscriber;
    const timer = setInterval(() => {
      attempts += 1;
      if (typeof subscribe === 'function') {
        clearInterval(timer);
        log('subscribe became available after', attempts, 'retries');
        unsubscriber = subscribe(eventName, callback);
        return;
      }
      if (attempts > 50) {
        clearInterval(timer);
        console.error('subscribe never became available');
      }
    }, 100);
    return () => unsubscriber?.();
  }

  function getSelection() {
    if (window.CraftCellarsLocation?.getSelection?.()) {
      return window.CraftCellarsLocation.getSelection();
    }

    try {
      const raw = localStorage.getItem('craft_cellars_selected_location');
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      log('Failed to read location selection from localStorage', error);
      return null;
    }
  }

  function getConfig() {
    return window.craftCellarsLocationConfig || null;
  }

  function openLocationModal() {
    if (typeof window.CraftCellarsLocation?.openModal === 'function') {
      window.CraftCellarsLocation.openModal();
      return true;
    }

    const modal = document.querySelector('location-selector-modal');
    if (modal?.show) {
      modal.show();
      return true;
    }

    return false;
  }

  function annotateStoreLinks(text) {
    return String(text)
      .replace(
        /(?<!Unavailable for )\bPickup at Northland\b/g,
        '<button type="button" class="location-inventory__link" data-location-key="location_2">Pickup at Northland</button>'
      )
      .replace(
        /(?<!Unavailable for )\bShipping & Pickup at 32nd Ave\b/g,
        '<button type="button" class="location-inventory__link" data-location-key="location_1">Shipping & Pickup at 32nd Ave</button>'
      );
  }

  function normalizeLocationId(id) {
    return window.CraftCellarsStorefrontApi?.normalizeLocationId?.(id) ?? String(id ?? '');
  }

  function normalizeName(name) {
    return String(name || '')
      .toLowerCase()
      .trim();
  }

  function findAvailability(availabilities, locationConfig, config, shopLocations) {
    if (!availabilities?.length || !locationConfig) return null;

    const targetId = normalizeLocationId(locationConfig.id);
    if (targetId) {
      const byId = availabilities.find(
        entry => normalizeLocationId(entry.location_id) === targetId
      );
      if (byId) return byId;
    }

    const namesToMatch = [
      locationConfig.name,
      locationConfig.shopifyName,
      locationConfig.label,
      locationConfig.address,
    ]
      .filter(Boolean)
      .map(normalizeName);

    const matchedAvailability = availabilities.find(entry =>
      namesToMatch.some(
        name =>
          normalizeName(entry.location_name) === name ||
          normalizeName(entry.location_name).includes(name) ||
          name.includes(normalizeName(entry.location_name))
      )
    );

    if (matchedAvailability) return matchedAvailability;

    // If shopLocations were embedded in the page (from Liquid), try matching by their name -> id
    if (shopLocations?.length) {
      const matchedShop = shopLocations.find(sl => namesToMatch.includes(normalizeName(sl.name)));
      if (matchedShop) {
        const byShopId = availabilities.find(
          entry => normalizeLocationId(entry.location_id) === normalizeLocationId(matchedShop.id)
        );
        if (byShopId) return byShopId;
      }
    }

    return null;
  }

  function resolveConfiguredLocationAvailability(
    availabilities,
    locationConfig,
    config,
    shopLocations,
    locationKey,
    fallbackName
  ) {
    const entry = locationConfig
      ? findAvailability(availabilities, locationConfig, config, shopLocations)
      : null;

    let quantity = null;
    if (entry && typeof entry.quantity_available === 'number') {
      quantity = Math.max(0, entry.quantity_available);
    } else if (entry) {
      quantity = entry.available ? 1 : 0;
    }

    return {
      locationKey,
      location_id: normalizeLocationId(locationConfig?.id || entry?.location_id),
      location_name:
        locationConfig?.name || locationConfig?.shopifyName || entry?.location_name || fallbackName,
      available: typeof quantity === 'number' ? quantity > 0 : false,
      quantity_available: quantity,
      source: 'location_entry',
    };
  }

  function resolveBothLocationsInventory(availabilities, config, shopLocations, totalQuantity) {
    const api = window.CraftCellarsStorefrontApi;
    if (api?.resolveBothLocationsInventory) {
      return api.resolveBothLocationsInventory(
        availabilities,
        config,
        shopLocations,
        totalQuantity
      );
    }

    // Mirror the shared API resolver so the PDP fallback behaves the same way
    // even if the helper script is unavailable for any reason.
    const pickup = resolveConfiguredLocationAvailability(
      availabilities,
      config?.locations?.location_2,
      config,
      shopLocations,
      'location_2',
      'Pickup'
    );
    const shipping = resolveConfiguredLocationAvailability(
      availabilities,
      config?.locations?.location_1,
      config,
      shopLocations,
      'location_1',
      'Shipping'
    );

    return {
      location_1: shipping,
      location_2: pickup,
      byKey: {
        location_1: shipping,
        location_2: pickup,
      },
    };
  }

  function isInStock(availability) {
    if (!availability) return false;
    if (window.CraftCellarsStorefrontApi?.isInStock) {
      return window.CraftCellarsStorefrontApi.isInStock(availability);
    }
    if (typeof availability.quantity_available === 'number') {
      return availability.quantity_available > 0;
    }
    return Boolean(availability.available);
  }

  const DEFAULT_UNAVAILABLE_MESSAGES = {
    location_1:
      'Unavailable for Shipping & Pickup at 32nd Ave. Available for Craft Cellars Northland.',
    location_2: 'Unavailable for Pickup at Northland. Available for Main Store.',
  };

  const BOTH_LOCATIONS_OUT_MESSAGE = 'No stock available';

  function bothLocationsOutOfStock(resolved) {
    return !isInStock(resolved?.location_1) && !isInStock(resolved?.location_2);
  }

  function getBothLocationsOutMessage(messages) {
    return messages?.outOfStock || BOTH_LOCATIONS_OUT_MESSAGE;
  }

  function getLocationUnavailableMessage(selectionKey, messages) {
    if (selectionKey === 'location_1' && messages?.location1Unavailable) {
      return messages.location1Unavailable;
    }
    if (selectionKey === 'location_2' && messages?.location2Unavailable) {
      return messages.location2Unavailable;
    }
    return (
      DEFAULT_UNAVAILABLE_MESSAGES[selectionKey] ||
      messages?.outOfStock ||
      'Out of stock at your selected store'
    );
  }

  function replacePlaceholders(template, vars) {
    if (!template) return '';
    let result = template.replace(/\[(\w+)\]/g, (_, key) => vars[key] ?? '');
    result = result.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => vars[key] ?? '');
    return result;
  }

  if (!customElements.get('location-inventory')) {
    customElements.define(
      'location-inventory',
      class LocationInventory extends HTMLElement {
        constructor() {
          super();
          this.onVariantChange = this.onVariantChange.bind(this);
          this.onLocationSelected = this.onLocationSelected.bind(this);
          this.onInventoryLinkClick = this.onInventoryLinkClick.bind(this);
          this._updateRequestId = 0;
        }

        connectedCallback() {
          log('location-inventory connectedCallback', {
            id: this.id,
            dataId: this.dataset.dataId,
            sectionId: this.dataset.sectionId,
            customElementRegistered: true,
          });

          this.showPlaceholder('Store availability message appears here (loading...)');

          const dataEl = document.getElementById(this.dataset.dataId);
          if (!dataEl) {
            console.error('Data script not found:', this.dataset.dataId);
            this.showPlaceholder('ERROR: inventory data not found on page');
            return;
          }

          this.loadMessages();
          log('Messages loaded', this.messages);

          try {
            this.inventoryData = JSON.parse(dataEl.textContent);
            log('Embedded inventory data', this.inventoryData);
          } catch (e) {
            console.error('JSON parse error', e, dataEl.textContent);
            this.showPlaceholder('ERROR: could not parse inventory data');
            return;
          }

          log('Startup state', {
            config: getConfig(),
            selection: getSelection(),
            storefrontEnabled: window.CraftCellarsStorefrontApi?.isEnabled?.(),
            variantId: this.getCurrentVariantId(),
          });

          this.scheduleUpdate();

          if (typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribeVariant = safeSubscribe(
              PUB_SUB_EVENTS.variantChange,
              this.onVariantChange.bind(this)
            );
          } else {
            console.warn('PUB_SUB_EVENTS not available');
          }

          const variantInput = this.closest('product-info')?.querySelector('input[name="id"]');
          variantInput?.addEventListener('change', () => {
            log('variant input change detected');
            this.update();
          });

          document.addEventListener('craft-cellars:location-selected', this.onLocationSelected);
          this.addEventListener('click', this.onInventoryLinkClick);
        }

        disconnectedCallback() {
          this.unsubscribeVariant?.();
          this.removeEventListener('click', this.onInventoryLinkClick);
          document.removeEventListener('craft-cellars:location-selected', this.onLocationSelected);
        }

        showPlaceholder(text) {
          this.classList.remove('hidden');
          this.classList.add('location-inventory--debug');
          this.innerHTML = ` `;
        }

        loadMessages() {
          const messagesEl = document.getElementById(this.dataset.messagesId);
          if (messagesEl) {
            try {
              this.messages = JSON.parse(messagesEl.textContent);
              return;
            } catch (e) {
              console.warn('Messages JSON parse failed', e);
            }
          }

          this.messages = {
            inStock: this.dataset.inStockMessage,
            outOfStock: this.dataset.outOfStockMessage,
            alternate: this.dataset.alternateMessage,
            custom: this.dataset.customMessage,
            noSelection: this.dataset.noSelectionMessage,
          };
        }

        scheduleUpdate(variantId) {
          const run = () => this.update(variantId);
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', run, { once: true });
          } else {
            run();
          }
        }

        onLocationSelected(event) {
          log('location-selected event', event.detail);
          this.update();
        }

        onVariantChange({ data }) {
          log('variantChange event', data);
          const publishedSection = String(data?.sectionId || '');
          const mySection = String(this.dataset.sectionId || '');
          if (publishedSection && mySection && publishedSection !== mySection) {
            log('variantChange ignored (different section)', { publishedSection, mySection });
            return;
          }
          this.update(data?.variant?.id);
        }

        isVariantPurchasable() {
          const selected = this.closest('product-info')?.querySelector('[data-selected-variant]');
          if (selected?.textContent) {
            try {
              return JSON.parse(selected.textContent).available !== false;
            } catch (e) {
              return true;
            }
          }

          const variantInput = this.closest('product-info')?.querySelector('input[name="id"]');
          return variantInput ? !variantInput.disabled : true;
        }

        isVariantPhysical() {
          const selected = this.closest('product-info')?.querySelector('[data-selected-variant]');
          if (selected?.textContent) {
            try {
              const variant = JSON.parse(selected.textContent);
              return variant.requires_shipping !== false;
            } catch (e) {
              return true;
            }
          }
          return true;
        }

        setLocationPurchaseState(canPurchaseAtLocation) {
          const productInfo = this.closest('product-info');
          const canPurchase = Boolean(canPurchaseAtLocation) && this.isVariantPurchasable();

          // Exclude the Notify button
          const addToCartButtons =
            productInfo?.querySelectorAll('.product-form__submit:not(#BIS_trigger)') ?? [];

          const notifyBtn = document.getElementById('BIS_trigger');

          addToCartButtons.forEach(btn => {
            const textEl = btn.querySelector('span');

            if (!canPurchase) {
              btn.classList.add('out-of-stock-location');
              btn.setAttribute('disabled', 'disabled');
              btn.setAttribute('aria-disabled', 'true');

              if (textEl) {
                textEl.textContent = 'SOLD OUT';
              } else {
                btn.textContent = 'SOLD OUT';
              }

              // Show Notify Me button
              if (notifyBtn) {
                notifyBtn.style.display = '';
                notifyBtn.removeAttribute('disabled');
                notifyBtn.removeAttribute('aria-disabled');
                notifyBtn.textContent = 'NOTIFY ME WHEN AVAILABLE';
              }
            } else {
              btn.classList.remove('out-of-stock-location');

              if (this.isVariantPurchasable()) {
                btn.removeAttribute('disabled');
                btn.removeAttribute('aria-disabled');
              }

              if (textEl) {
                textEl.textContent = 'ADD TO CART';
              } else {
                btn.textContent = 'ADD TO CART';
              }

              // Hide Notify Me button
              if (notifyBtn) {
                notifyBtn.style.display = 'none';
              }
            }
          });
        }

        getVariantTotalQuantity(variantId, variantQuantityFromApi) {
          if (typeof variantQuantityFromApi === 'number') {
            return variantQuantityFromApi;
          }

          const selected = this.closest('product-info')?.querySelector('[data-selected-variant]');
          if (selected?.textContent) {
            try {
              const variant = JSON.parse(selected.textContent);
              if (
                Number(variant.id) === Number(variantId) &&
                typeof variant.inventory_quantity === 'number'
              ) {
                return variant.inventory_quantity;
              }
            } catch (e) {
              log('Failed to parse selected variant for inventory_quantity', e);
            }
          }

          const embedded = this.inventoryData?.variants?.find(
            entry => Number(entry.id) === Number(variantId)
          );
          if (typeof embedded?.inventory_quantity === 'number') {
            return embedded.inventory_quantity;
          }

          return null;
        }

        getCurrentVariantId(explicitVariantId) {
          if (explicitVariantId != null && explicitVariantId !== '') {
            return Number(explicitVariantId);
          }

          const selected = this.closest('product-info')?.querySelector('[data-selected-variant]');
          if (selected?.textContent) {
            try {
              return Number(JSON.parse(selected.textContent).id);
            } catch (e) {
              return null;
            }
          }

          const input = this.closest('product-info')?.querySelector('input[name="id"]');
          return input?.value ? Number(input.value) : null;
        }

        getVariantAvailabilities(variantId) {
          const variant = this.inventoryData?.variants?.find(
            entry => Number(entry.id) === Number(variantId)
          );
          const availabilities = variant?.availabilities || [];
          log('Liquid embedded availabilities', { variantId, availabilities });
          return availabilities;
        }

        async fetchAvailabilities(variantId) {
          let source = 'none';

          if (window.CraftCellarsStorefrontApi?.isEnabled?.()) {
            try {
              const apiPayload =
                await window.CraftCellarsStorefrontApi.fetchVariantAvailabilities(variantId);
              const apiAvailabilities = apiPayload?.availabilities ?? apiPayload;
              if (apiAvailabilities?.length) {
                source = 'storefront-api';
                log('Using availability source:', source, apiAvailabilities);
                return {
                  availabilities: apiAvailabilities,
                  variantQuantityAvailable: apiPayload?.variantQuantityAvailable ?? null,
                };
              }
              log('Storefront API returned empty, using embedded Liquid');
            } catch (error) {
              console.warn('Storefront API failed', error);
            }
          } else {
            log('Storefront API disabled, using embedded Liquid');
          }

          source = 'liquid-embedded';
          const embeddedVariant = this.inventoryData?.variants?.find(
            entry => Number(entry.id) === Number(variantId)
          );
          const result = {
            availabilities: embeddedVariant?.availabilities || [],
            variantQuantityAvailable:
              typeof embeddedVariant?.inventory_quantity === 'number'
                ? embeddedVariant.inventory_quantity
                : null,
          };
          log('Using availability source:', source, result);
          return result;
        }

        async update(explicitVariantId) {
          const requestId = ++this._updateRequestId;
          const selection = getSelection();
          const config = getConfig();
          const variantId = this.getCurrentVariantId(explicitVariantId);

          log('update() called', { requestId, selection, variantId, config });

          if (!variantId) {
            this.setLocationPurchaseState(false);
            this.showPlaceholder('Waiting for product variant...');
            return;
          }

          if (!this.isVariantPhysical()) {
            log('Digital product detected, skipping location inventory');
            this.classList.add('hidden');
            this.setLocationPurchaseState(true);
            return;
          }

          if (selection.type === 'custom') {
            this.setLocationPurchaseState(true);
            this.renderMessage(this.messages?.custom, 'location-inventory--in');
            return;
          }

          this.classList.remove('hidden', 'location-inventory--debug');
          this.setAttribute('aria-busy', 'true');
          this.showPlaceholder('Checking store stock...');

          let availabilities = [];
          let variantQuantityAvailable = null;
          try {
            const inventoryPayload = await this.fetchAvailabilities(variantId);
            availabilities = inventoryPayload?.availabilities || [];
            variantQuantityAvailable = inventoryPayload?.variantQuantityAvailable ?? null;
          } catch (e) {
            console.error('fetchAvailabilities error', e);
          }

          if (requestId !== this._updateRequestId) {
            log('Stale update request, skipping render', requestId);
            return;
          }
          this.removeAttribute('aria-busy');

          const totalQuantity = this.getVariantTotalQuantity(variantId, variantQuantityAvailable);
          const resolved = resolveBothLocationsInventory(
            availabilities,
            config,
            this.inventoryData?.shopLocations,
            totalQuantity
          );
          const selectedAvailability =
            resolved?.byKey?.[selection.key] ?? resolved?.[selection.key] ?? null;
          const selectedInStock = isInStock(selectedAvailability);

          // console.log('[Craft Cellars Location] Independent location stock', {
          //   variantId,
          //   selectionKey: selection.key,
          //   totalQuantity,
          //   shipping: resolved?.location_1,
          //   pickup: resolved?.location_2,
          //   selectedAvailability,
          //   selectedInStock,
          //   availabilities,
          // });

          if (selectedInStock) {
            this.setLocationPurchaseState(true);
            this.renderMessage('', '');
            return;
          }

          const message = bothLocationsOutOfStock(resolved)
            ? getBothLocationsOutMessage(this.messages)
            : getLocationUnavailableMessage(selection.key, this.messages);
          this.setLocationPurchaseState(false);
          this.renderMessage(message, 'location-inventory--out');
        }

        onInventoryLinkClick(event) {
          const btn = event.target.closest('.location-inventory__link');
          if (!btn) return;
          event.preventDefault();

          const locationKey = btn.dataset.locationKey;
          if (!locationKey) return;

          const config = getConfig();
          const locationConfig = config?.locations?.[locationKey];
          if (!locationConfig) return;

          const selection = {
            key: locationKey,
            type: locationConfig.type || 'shop',
            id: locationConfig.id || '',
            name: locationConfig.name || locationConfig.shopifyName || '',
          };

          window.CraftCellarsLocation.setSelection(selection);

          // Update card highlights in the modal (if it's in the DOM) to stay in sync
          document.querySelectorAll('[data-location-key]').forEach(card => {
            card.classList.toggle('is-selected', card.dataset.locationKey === locationKey);
          });

          window.CraftCellarsLocation?.syncHeader?.();
        }

        renderMessage(text, statusClass) {
          log('renderMessage', { text, statusClass });

          if (!text) {
            this.classList.add('hidden');
            this.innerHTML = '';
            return;
          }

          this.classList.remove(
            'hidden',
            'location-inventory--debug',
            'location-inventory--in',
            'location-inventory--alternate',
            'location-inventory--out',
            'location-inventory--muted'
          );
          this.classList.add(statusClass);

          const iconColor =
            statusClass === 'location-inventory--in'
              ? 'rgb(62, 214, 96)'
              : statusClass === 'location-inventory--alternate'
                ? 'rgb(158, 17, 38)'
                : 'rgb(200, 200, 200)';

          this.innerHTML = `
            <span class="location-inventory__text">${annotateStoreLinks(text)}</span>
          `;
        }
      }
    );

    log('location-inventory custom element registered');
  } else {
    log('location-inventory already registered');
  }

  // document.addEventListener('DOMContentLoaded', () => {
  //   const el = document.querySelector('location-inventory');
  //   log('DOMContentLoaded check', {
  //     elementFound: Boolean(el),
  //     elementId: el?.id,
  //     config: window.craftCellarsLocationConfig,
  //     selectionRaw: localStorage.getItem('craft_cellars_selected_location'),
  //     selection: getSelection(),
  //     storefrontApi: Boolean(window.CraftCellarsStorefrontApi),
  //   });
  // });
})();
